import { get, put, post, del, upload } from '@api/request';

export async function fetchSearchParams(
  { litigationService },
  authenticationToken,
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameters`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function fetchSearchParam(
  { litigationService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameter/${searchParamID}`;
  const { body } = await get(authenticationToken, url);
  return body;
}

export async function insertSearchParam(
  { litigationService },
  authenticationToken,
  { payload },
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameters`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
}

export async function updateSearchParam(
  { litigationService },
  authenticationToken,
  { searchParamID, payload },
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameter/${searchParamID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
}

export async function duplicateSearchParam(
  { litigationService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameter/${searchParamID}/duplicate`;
  const { body } = await post(authenticationToken, url);
  return body;
}

export async function deleteSearchParam(
  { litigationService },
  authenticationToken,
  { searchParamID },
) {
  const url = `${litigationService}/litigation/case/propertyoffer/searchparameter/${searchParamID}`;
  const { body } = await del(authenticationToken, url);
  return body;
}

export const search = async (
  { litigationService },
  authenticationToken,
  { pageSize, page, sortField, sortDirection, filterModel, payload },
) => {
  let url = `${litigationService}/litigation/case/propertyoffer/search?page=${page}&pageSize=${pageSize}`;

  if (sortField && sortDirection) {
    url += `&sortField=${sortField}&sortDirection=${sortDirection}`;
  }

  if (filterModel && Object.keys(filterModel).length >= 0) {
    Object.keys(filterModel).forEach(filter => {
      url += `&searchField=${filter}&searchText=${filterModel[filter].value}`;
    });
  }

  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const getCaseFiles = async (
  { litigationService },
  authenticationToken,
  { caseID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/files`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const addPropOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffer/${offerID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const updatePropOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffer/${offerID}/files`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const deletePropOfferFile = async (
  { litigationService },
  authenticationToken,
  { payload, caseID, offerID, s3ID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffer/${offerID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadPropOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, caseID, s3ID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffer/${offerID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};

export const fetchCanFinalize = async (
  { litigationService },
  authenticationToken,
  { caseID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/canfinalizebasedoffers`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchPropertyOffer = async (
  { litigationService },
  authenticationToken,
  { offerID },
) => {
  const url = `${litigationService}/litigation/case/propertyoffer/${offerID}`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchPropertyOffersAll = async (
  { litigationService },
  authenticationToken,
  { caseID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffersall`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const updateFinalValue = async (
  { litigationService },
  authenticationToken,
  { payload },
) => {
  const url = `${litigationService}/litigation/case/property/finalvalue`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const publishPropertyOfferInd = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/publish`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const updatePropertyOfferInd = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/propertyoffer/${caseID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const updatePropertyOfferAgg = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/propertyofferaggregate/${caseID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const createPropertyOfferInd = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/individual`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const createPropertyOfferAgg = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/aggregate`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const fetchCaseOfferActivity = async (
  { litigationService },
  authenticationToken,
  { caseID, offerID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffer/${offerID}/activity`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const reopenPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/reopen`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const withdrawPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/withdraw`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const cancelPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/cancel`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const publishPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/publish`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const acceptPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/accept`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const rejectPropertyOffers = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/propertyoffers/reject`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const fetchOffer = async (
  { litigationService },
  authenticationToken,
  { offerID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchOffers = async (
  { litigationService },
  authenticationToken,
  { caseID },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/offers`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, s3ID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/file/${s3ID}`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchOfferFiles = async (
  { litigationService },
  authenticationToken,
  { offerID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/files`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchCodefileFilingParty = async (
  { appraisalService },
  authenticationToken,
) => {
  const url = `${appraisalService}/appraisal/codefiles?type=Litigation&name=Filing Party`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchCodefileOfferStatus = async (
  { appraisalService },
  authenticationToken,
) => {
  const url = `${appraisalService}/appraisal/codefiles?type=Litigation&name=Offer Status`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const fetchCodefileOfferType = async (
  { appraisalService },
  authenticationToken,
) => {
  const url = `${appraisalService}/appraisal/codefiles?type=Litigation&name=Offer Type`;
  const { body } = await get(authenticationToken, url);
  return body;
};

export const insertOffer = async (
  { litigationService },
  authenticationToken,
  { caseID, payload },
) => {
  const url = `${litigationService}/litigation/case/${caseID}/offers`;
  const { body } = await post(authenticationToken, url, payload);
  return body;
};

export const insertOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, payload },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/files`;
  const { body } = await upload(authenticationToken, url, payload);
  return body;
};

export const updateOffer = async (
  { litigationService },
  authenticationToken,
  { offerID, payload },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const updateOfferTags = async (
  { litigationService },
  authenticationToken,
  { offerID, payload },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/tags`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const updateOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, payload },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const deleteOffer = async (
  { litigationService },
  authenticationToken,
  { offerID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}`;
  const { body } = await del(authenticationToken, url);
  return body;
};

export const deleteOfferFile = async (
  { litigationService },
  authenticationToken,
  { payload, offerID, s3ID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/file/${s3ID}`;
  const { body } = await put(authenticationToken, url, payload);
  return body;
};

export const downloadOfferFile = async (
  { litigationService },
  authenticationToken,
  { offerID, s3ID },
) => {
  const url = `${litigationService}/litigation/offer/${offerID}/file/${s3ID}`;

  const anchor = document.createElement('a');
  document.body.appendChild(anchor);

  const headers = new Headers();
  headers.append('Authorization', authenticationToken);

  return await fetch(url, { headers })
    .then(response => response.blob())
    .then(blobby => {
      const objectUrl = window.URL.createObjectURL(blobby);

      anchor.href = objectUrl;
      anchor.download = s3ID;
      anchor.click();

      window.URL.revokeObjectURL(objectUrl);
    });
};
